import { CommonModule } from '@angular/common';
import { Component, EventEmitter, forwardRef, Input, OnInit, Output } from '@angular/core';
import { FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Question, QuestionCondition, Questionnaire } from '@app/core/models/questionnaire.model';
import { QuestionConditionParameter } from '@app/core/enums/questionnaire.enums';

const RATING_PARAM = 'RATING';
const STAR_COUNT = 5;

@Component({
  selector: 'app-social-review',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './social-review.html',
  styleUrl: './social-review.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SocialReview),
      multi: true,
    },
  ],
})
export class SocialReview implements OnInit {
  @Input() questionnaire: Questionnaire | null = null;
  @Input() question: Question | null = null;
  @Input() questionNumber: number = 0;
  @Input() isInvalid: boolean = false;
  @Input() errorMessage: string = '';
  @Input() formControl?: FormControl;

  @Output() answerChange = new EventEmitter<{ questionId: string; score: number }>();

  selectedRating: number | null = null;
  disabled = false;
  readonly ratingStars = Array.from({ length: STAR_COUNT }, (_, i) => i + 1);

  onChange = (value: number | null) => {};
  onTouched = () => {};

  ngOnInit(): void {
    if (this.formControl) {
      const v = this.formControl.value;
      this.selectedRating = v != null && typeof v === 'number' ? v : null;
      this.formControl.valueChanges.subscribe((value) => {
        this.selectedRating = value != null && typeof value === 'number' ? value : null;
      });
    }
  }

  writeValue(value: number | null): void {
    this.selectedRating = value != null ? value : null;
  }

  registerOnChange(fn: (v: number | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  /** First question condition with parameter RATING, if any */
  getRatingCondition(): QuestionCondition | null {
    const conditions = this.question?.questionConditions;
    if (!conditions?.length) return null;
    return conditions.find((c) => c?.parameter === RATING_PARAM || c?.parameter === QuestionConditionParameter.RATING) ?? null;
  }

  get showRatingStars(): boolean {
    return this.getRatingCondition() != null;
  }

  selectRating(value: number, event: Event): void {
    event.stopPropagation();
    if (this.disabled) return;
    this.selectedRating = value;
    this.onChange(value);
    this.onTouched();

    if (this.question?.questionId) {
      this.answerChange.emit({ questionId: this.question.questionId, score: value });
    }
    if (this.formControl) {
      this.formControl.setValue(value, { emitEvent: true });
      this.formControl.markAsTouched();
    }
  }
}
